import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { cget, cset } from "@/lib/cached";
import { LOGO_CROPPED } from "@/components/Logo";

export default function Nav() {
  const { totals, setOpen } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Read the last known-good announcement synchronously so the bar does NOT
  // flash the previous default text before the API resolves.
  const [announcement, setAnnouncement] = useState(() => cget("imu_announcement"));
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchWrap = useRef(null);

  useEffect(() => {
    api.get("/settings/announcement").then(({ data }) => {
      if (data?.text) {
        setAnnouncement(data.text);
        cset("imu_announcement", data.text);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    let cancel = false;
    api.get("/products", { params: { q } }).then(({ data }) => { if (!cancel) setSuggestions(data.slice(0, 6)); });
    return () => { cancel = true; };
  }, [q]);

  useEffect(() => {
    const handler = (e) => {
      if (searchWrap.current && !searchWrap.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const linkCls = ({ isActive }) =>
    "link-underline text-sm tracking-wide " + (isActive ? "text-ink-900" : "text-ink-700");

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setShowResults(false);
    nav(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      {/* Announcement bar — hidden entirely until first load to avoid FOUC */}
      {announcement ? (
        <div className="bg-ink-900 text-white text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.28em] uppercase text-center py-2 px-3 truncate" data-testid="announcement-bar">
          {announcement}
        </div>
      ) : (
        <div className="bg-ink-900 h-8" aria-hidden="true" />
      )}

      <header className="sticky top-0 z-40 bg-white border-b hairline">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 lg:h-24 flex items-center justify-between gap-3 lg:gap-6">
          <button className="lg:hidden shrink-0" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 lg:pr-64 xl:pr-80">
            <NavLink to="/shop" className={linkCls} data-testid="nav-shop">Shop</NavLink>
            <NavLink to="/about" className={linkCls} data-testid="nav-about">About Us</NavLink>
            <NavLink to="/dermatologists" className={linkCls} data-testid="nav-derm">Talk to a Dermatologist</NavLink>
            <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact Us</NavLink>
            <NavLink to="/track" className={linkCls} data-testid="nav-track">Track Orders</NavLink>
          </nav>

          <Link to="/" className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 shrink-0" data-testid="brand-logo-link">
            <img src={LOGO_CROPPED} alt="IMMUSKI" className="h-12 sm:h-16 lg:h-20 w-auto" data-testid="brand-logo" />
          </Link>

          <div className="flex items-center gap-3 sm:gap-4">
            {/* Search (desktop only) */}
            <div ref={searchWrap} className="relative hidden md:block">
              <form onSubmit={submitSearch} className="flex items-center gap-2 border hairline px-3 py-2 bg-white w-48 xl:w-64">
                <Search size={14} className="text-ink-500" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
                  onFocus={() => setShowResults(true)}
                  placeholder="Search products…"
                  className="w-full bg-transparent outline-none text-sm"
                  data-testid="nav-search-input"
                />
              </form>
              {showResults && suggestions.length > 0 && (
                <div className="absolute right-0 top-full mt-1 w-72 sm:w-80 bg-white border hairline shadow-lg z-50" data-testid="nav-search-results">
                  {suggestions.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.slug}`}
                      onClick={() => { setShowResults(false); setQ(""); }}
                      className="flex gap-3 p-3 border-b hairline hover:bg-cream-200 last:border-b-0"
                      data-testid={`nav-search-result-${p.slug}`}
                    >
                      <img src={p.hero_image} alt="" className="w-12 h-14 object-cover bg-cream-200" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink-900 font-display truncate">{p.name}</div>
                        <div className="text-[11px] text-ink-500 truncate">{p.subtitle}</div>
                      </div>
                      <div className="text-sm tabular-nums">₹{p.price.toFixed(0)}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile search icon → opens mobile menu */}
            <button onClick={() => setMobileOpen(true)} className="md:hidden text-ink-900" aria-label="Search" data-testid="mobile-search-toggle">
              <Search size={20} />
            </button>

            <Link to={user ? "/account" : "/auth"} className="flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900" data-testid="nav-account">
              <User size={18} />
            </Link>
            <button onClick={() => setOpen(true)} className="relative flex items-center gap-1 text-ink-900" data-testid="nav-cart">
              <ShoppingBag size={20} />
              <span className="text-xs sm:text-sm tabular-nums">({totals.count})</span>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t hairline bg-white" data-testid="mobile-menu">
            <div className="px-6 py-6 flex flex-col gap-4">
              <form onSubmit={submitSearch} className="flex items-center gap-2 border hairline px-3 py-2">
                <Search size={14} className="text-ink-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="w-full bg-transparent outline-none text-sm" data-testid="mobile-search-input" />
              </form>
              <NavLink to="/shop" onClick={() => setMobileOpen(false)} className="text-sm">Shop</NavLink>
              <NavLink to="/about" onClick={() => setMobileOpen(false)} className="text-sm">About Us</NavLink>
              <NavLink to="/dermatologists" onClick={() => setMobileOpen(false)} className="text-sm">Talk to a Dermatologist</NavLink>
              <NavLink to="/contact" onClick={() => setMobileOpen(false)} className="text-sm">Contact Us</NavLink>
              <NavLink to="/track" onClick={() => setMobileOpen(false)} className="text-sm">Track Orders</NavLink>
              <NavLink to={user ? "/account" : "/auth"} onClick={() => setMobileOpen(false)} className="text-sm">{user ? "Account" : "Sign in"}</NavLink>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
